/**
 * Maneja la visualización y el control de las capas superpuestas (overlay layers) en el mapa.
 * Permite activar/desactivar capas individuales o grupos de capas, y expandir/contraer grupos de capas.
 * Utiliza la configuración del contexto `ConfigContext` y el contexto del mapa `MapContext`.
 * @component
 * 
 * @example
 * <ControlerLayerOverlay />
 * 
 * @author Eva Rabasco
 */
import React, { useState, useContext, useEffect, useMemo, useCallback, memo } from 'react';
import MapContext from '../Map/MapContext'; // Contexto para el mapa
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext';
import { useTranslation } from 'react-i18next';
import 'bootstrap-icons/font/bootstrap-icons.css'; // Iconos de Bootstrap
import './ControlerLayerOverlay.css'
import LayerTools from '../LayersTools/LayersTools';
import { getSvg } from '../../utils/misc';

const ControlerLayerOverlay = () => {
    // Obtener la configuración global de capas (config) y el mapa actual del contexto
    const { 
        config, 
        visibleOverlayLayers, 
        setVisibleOverlayLayers, 
        opacities, 
        setOpacities 
    } = useGlobalVariablesContext();
    const { t } = useTranslation();

    const { map } = useContext(MapContext);

    // Memoizar el estado inicial de expansión de grupos
    const expandedGroupsInitialState = useMemo(() => {
        if (!config?.GROUPS) return {};
        const initialExpandedState = {};
        config.GROUPS.forEach(group => {
            initialExpandedState[group.name] = group.expanded || false;
        });
        return initialExpandedState;
    }, [config?.GROUPS]);

    // Estado para gestionar la expansión de los grupos de capas
    const [expandedGroups, setExpandedGroups] = useState(expandedGroupsInitialState);

    // Si no hay mapa o configuración, no renderizar nada
    if (!map || !config) return null;

    // Memoizar las capas overlay para evitar filtrado en cada render
    const overlayLayers = useMemo(() => 
        config?.LAYERS?.filter((layer) => layer.type === 'overlay' && !layer.tematica) || [],
        [config?.LAYERS]
    );

    // Memoizar los grupos para evitar procesamiento en cada render
    const groups = useMemo(() => config?.GROUPS || [], [config?.GROUPS]);

    // Función optimizada para cambiar la opacidad de una capa
    const handleOpacityChange = useCallback((layerName, newOpacity) => {
        console.log('nueva opacidad', opacities, layerName);
        setOpacities((prevOpacities) => ({
            ...prevOpacities,
            [layerName]: newOpacity
        }));

        if (map) {
            const capa = map
                .getLayers()
                .getArray()
                .find((layer) => layer.get('name') === layerName);
            if (capa) capa.setOpacity(newOpacity);
        }
    }, [opacities, map]);

    // Función optimizada para alternar la visibilidad de una capa individual (overlay)
    const handleLayerToggleOverlay = useCallback((layerName) => {
        // Comprobamos si la capa ya está visible en las capas activas
        const newVisibleOverlayLayers = layerName in visibleOverlayLayers
            // Si la capa está visible, la eliminamos de la lista de capas visibles
            ? Object.keys(visibleOverlayLayers)
                .filter((key) => key !== layerName)  // Filtra la capa que queremos ocultar
                .reduce((acc, key) => {
                    acc[key] = visibleOverlayLayers[key]; // Copia las capas restantes
                    return acc;
                }, {})
            // Si la capa no está visible, la agregamos a la lista de capas visibles
            : (() => {
                // Buscar la capa en `config.LAYERS` o en los grupos para obtener su `source`
                const layer =
                    config.LAYERS.find((l) => l.name === layerName) ||
                    config.GROUPS.flatMap((group) => group.layers).find((l) => l.name === layerName);

                return {
                    ...visibleOverlayLayers,
                    [layerName]: { source: layer?.source || null }  // Añadimos la capa con su `source`
                };
            })();

        setVisibleOverlayLayers(newVisibleOverlayLayers);

        // Buscamos la capa en el mapa por su nombre
        const capa = map.getLayers().getArray().find((layer) => layer.get('name') === layerName);
        // Si la capa se encuentra en el mapa, alternamos su visibilidad
        if (capa) capa.setVisible(!capa.getVisible());
    }, [visibleOverlayLayers, config, map]);


    // Función optimizada para alternar la visibilidad de las capas dentro de un grupo
    const handleLayerToggleGroup = useCallback((groupName) => {
        // Buscamos el grupo por su nombre en la configuración de grupos
        const group = config.GROUPS.find((group) => group.name === groupName);
        if (!group) return;

        // Obtenemos los nombres de todas las capas dentro del grupo
        const groupLayers = group.layers.map((layer) => layer.name);
        // Verificamos si todas las capas del grupo están visibles
        const allGroupLayersVisible = groupLayers.every((layerName) => layerName in visibleOverlayLayers);

        // Si todas las capas están visibles, las desmarcamos
        const newVisibleOverlayLayers = allGroupLayersVisible
            ? Object.keys(visibleOverlayLayers).reduce((acc, layerName) => {
                // Si la capa no pertenece al grupo, la mantenemos en la lista
                if (!groupLayers.includes(layerName)) {
                    acc[layerName] = visibleOverlayLayers[layerName];
                }
                return acc;
            }, {})
            // Si alguna capa no está visible, la añadimos
            : {
                ...visibleOverlayLayers,
                ...groupLayers.reduce((acc, layerName) => {
                    // Si la capa no está en las visibles, la agregamos
                    if (!(layerName in visibleOverlayLayers)) {
                        acc[layerName] = { source: null }; // Establecemos `source` como null para capas no visibles
                    }
                    return acc;
                }, {})
            };

        // Actualizamos el estado con la nueva lista de capas visibles
        setVisibleOverlayLayers(newVisibleOverlayLayers);

        // Cambiamos la visibilidad de cada capa dentro del grupo en el mapa
        group.layers.forEach((layer) => {
            const mapLayer = map.getLayers().getArray().find((l) => l.get('name') === layer.name);
            if (mapLayer) mapLayer.setVisible(!allGroupLayersVisible); // Alternamos la visibilidad de la capa
        });
    }, [config.GROUPS, visibleOverlayLayers, map]);


    // Función optimizada para alternar la expansión de un grupo de capas
    const toggleGroupExpansion = useCallback((groupName) => {
        setExpandedGroups((prevExpandedGroups) => ({
            ...prevExpandedGroups,
            [groupName]: !prevExpandedGroups[groupName], // Alterna entre expandir o contraer el grupo
        }));
    }, []);

    return (
        <div>
            {/* Renderizar las capas individuales (LAYERS) */}
            {overlayLayers.map((layer) => (

                <div className="layer-selector d-flex justify-content-between align-items-center" key={layer.name}>
                    <div className="d-flex align-items-center">
                        <input
                            type="checkbox"
                            id={layer.name}
                            checked={layer.name in visibleOverlayLayers}
                            onChange={() => handleLayerToggleOverlay(layer.name)}
                        />

                        {/* Imagen de la leyenda con color dinámico */}
                        <div className="erb-legend" dangerouslySetInnerHTML={{ __html: getSvg(layer.iconPath, layer.cluster?.style?.image?.fill?.color || layer.iconColor) }} />
                        <label htmlFor={layer.name} title={layer.description} className="layer-title ms-1">
                            {t(layer.title)}
                        </label>
                    </div>

                    {/* Herramientas de capa */}
                    <LayerTools
                        layer={layer}
                        opacities={opacities}
                        handleOpacityChange={handleOpacityChange}
                    />
                </div>
            ))}

            {/* Renderizar los grupos (GROUPS) */}
            {groups.map((group) => (
                <div key={group.name} className="layer-selector--group">
                    <div className="grupo d-flex align-items-center">
                        <input
                            type="checkbox"
                            id={group.name}
                            checked={group.layers.every((layer) => layer.name in visibleOverlayLayers)}
                            onChange={() => handleLayerToggleGroup(group.name)}
                        />
                        <i
                            className={`bi ${expandedGroups[group.name] ? 'bi-folder2-open' : 'bi-folder-fill'}`}
                            onClick={() => toggleGroupExpansion(group.name)}
                            style={{
                                cursor: 'pointer',
                                color: 'var(--overPrimaryColor)',
                                marginRight: '8px',
                                paddingLeft: '10px',
                                fontSize: '1.2em'
                            }}
                        ></i>
                        <label htmlFor={group.name} title={group.description} className="group-title">
                            {t(group.title)}
                        </label>
                    </div>

                    {/* Mostrar las capas dentro del grupo si está expandido */}
                    {expandedGroups[group.name] && (
                        <div className="subgrupos">
                            {group.layers.map((layer) => {
                                // Determinar el iconPath o el path predeterminado
                                const iconPath = layer.iconPath ? layer.iconPath : 'M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6';

                                return (
                                    <div className="layer-selector d-flex justify-content-between align-items-center" key={layer.name}>
                                        <div className="d-flex align-items-center">
                                            <input
                                                type="checkbox"
                                                id={layer.name}
                                                checked={layer.name in visibleOverlayLayers}
                                                onChange={() => handleLayerToggleOverlay(layer.name)}
                                            />
                                            {/* Imagen de la leyenda con color dinámico */}
                                            <div className="erb-legend" dangerouslySetInnerHTML={{ __html: getSvg(iconPath, layer.cluster?.style?.image?.fill?.color || layer.iconColor) }} />
                                            <label htmlFor={layer.name} title={layer.description} className="layer-title ms-1">
                                                {t(layer.title)}
                                            </label>
                                        </div>

                                        {/* Herramientas de capa */}
                                        <LayerTools
                                            layer={layer}
                                            opacities={opacities}
                                            handleOpacityChange={handleOpacityChange}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}

                </div>
            ))}
        </div>
    );
};

export default memo(ControlerLayerOverlay);
